/**
 * v3.identity — Identity Verification Pipeline (Bill 2026-05-18, BACKLOG-0068).
 *
 * Server: apps/PCBJxChangeApi/Functions/IdentityFunction.cs.
 *
 * Three routes, all Officer-role gated via AdminAuthService:
 *   POST /v3/identity/verify
 *     Creates an IdentityVerificationInquiry umbrella row. For Tier 1 (html-
 *     import interim) returns {inquiryId, clipboardPayload, nextStep} that
 *     the v3 Person Wizard uses to: clipboard.writeText(payload) →
 *     microsoftTeams.executeDeepLink(LN home) → FSR runs LN bookmarklet.
 *   POST /v3/identity/import-lexisnexis-html
 *     Ingests the FSR's exported LN HTML report. Parses via LnHtmlParser,
 *     persists to identity.IdentityProviderExtract (SourceMode='html-import')
 *     + 3 child tables, creates the umbrella IdentityVerificationSignal row.
 *   GET  /v3/identity/extracts/{id}
 *     Returns full Extract + ordered VerificationDetail / RedFlag /
 *     FraudWarning children for FSR review in the Identity panel.
 *
 * See docs/LexisNexis API Information/HTML-PARSING-ARCHITECTURE.md +
 * TIER1-IMPLEMENTATION-SYNTHESIS.md for the full architecture.
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// ──────────────────────────────────────────────────────────────────────────
// Request DTOs (mirror PCBJxChangeApi.Functions.IdentityFunction inputs)
// ──────────────────────────────────────────────────────────────────────────

export interface SubjectIdentity {
  firstName?: string;
  lastName?: string;
  middleInitial?: string;
  suffix?: string;
  ssn?: string;
  /** MM/DD/YYYY (preferred) or YYYY-MM-DD. */
  dob?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
  ipAddress?: string;
  licenseNumber?: string;
  licenseState?: string;
  // NRA passport (optional)
  passportMrzLine1?: string;
  passportMrzLine2?: string;
  passportGender?: string;
}

export interface VerifyInquiryInput {
  cartId?: string;
  customerStageId?: string;
  /** Production | UAT-Live | Test-Live | Sandbox. Defaults Production. */
  cartMode?: string;
  /** 'html-import' = run the LN-Bridge web report instead of the API (comparison run; officers
   *  in identity.verify.bridgeCompareOfficers only — ignored for anyone else). */
  preferredSourceMode?: 'html-import';
  subject: SubjectIdentity;
}

export interface ImportLexisNexisHtmlInput {
  inquiryId: string;
  html: string;
  /** Original filename if provided via file picker. */
  sourceFileName?: string;
  /** Override officer code; defaults to header-derived value. */
  officerCode?: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Response DTOs
// ──────────────────────────────────────────────────────────────────────────

/**
 * The exact JSON the v3 Person Wizard copies to the clipboard. Field names
 * match LN's form input IDs harvested by the discovery bookmarklet so the
 * LN-autofill bookmarklet can simply walk this object and set values.
 */
export interface LnClipboardPayload {
  v3InquiryId: string;
  FIRST_NAME?: string;
  LAST_NAME?: string;
  MI?: string;
  NAME_SUFFIX?: string;
  SSN?: string;
  DOB_M?: string;
  DOB_D?: string;
  DOB_Y?: string;
  STREET_ADDRESS?: string;
  CITY?: string;
  STATE?: string;
  ZIP?: string;
  PHONE?: string;
  EMAIL?: string;
  IP_ADD?: string;
  LICENSE_NUMBER?: string;
  LICENSE_STATE?: string;
  REFERENCE_CODE?: string;
  WATCH_LIST_OPT?: string;
  LBY_RADIUS_REF?: string;
  MACHINE_READONE?: string;
  MACHINE_READTWO?: string;
  PASSPORT_GENDER?: string;
}

export interface VerifyInquiryOutcome {
  success: boolean;
  inquiryId: string;
  sourceMode: 'html-import' | 'ddp-api' | 'internal-pipeline';
  /**
   * Present on an html-import outcome that was reached by FALLING BACK from
   * the direct LexisNexis connection: "{ErrorCode}: {message}" of the decline
   * (e.g. LN rejected national_id_number). Absent when html-import was the
   * first choice. Show it - a silent fallback hid a real defect for a day.
   */
  fallbackReason?: string;
  clipboardPayload: LnClipboardPayload;
  nextStep: {
    action: 'open-ln-and-run-bookmarklet';
    lnUrl: string;
  };
}

export interface ImportLexisNexisHtmlSummary {
  riskIndex?: number | null;
  riskLevelText?: string | null;
  fraudPointScore?: number | null;
  hasRedFlagSection?: boolean;
  redFlagCount: number;
  fraudWarningCount: number;
  verificationDetailCount: number;
  watchlistMode?: string | null;
  lexIdFound?: string | null;
  normalizedResult: 'Pass' | 'Warn' | 'Fail' | 'Inconclusive' | 'SourceUnavailable';
}

export interface ImportLexisNexisHtmlOutcome {
  success: boolean;
  inquiryId: string;
  extractId: string;
  summary: ImportLexisNexisHtmlSummary;
}

export interface IdentityProviderExtractRow {
  id: string;
  inquiryId: string;
  signalId?: number | null;
  sourceId: string;
  ingestedUtc: string;
  ingestedByOfficerCode?: string | null;
  sourceMode: string;
  sourceFileName?: string | null;
  parserVersion: string;
  // Headline scoring fields the Identity panel actually displays
  verificationRiskIndex?: number | null;
  verificationRiskLevelText?: string | null;
  verifSummary_NameAddrPhone?: string | null;
  verifSummary_NameAddrSsn?: string | null;
  verifSummary_DobMatchLevel?: string | null;
  potentialRiskIndicatorsJson?: string | null;
  watchlistResultsJson?: string | null;
  // Raw #watchlist container HTML. Non-empty with an empty watchlistResultsJson
  // = a CLEAN result ("No OFAC or Watchlist Matches"). The whole entity is
  // serialized server-side so these arrive on the wire already (Bill 2026-06-03).
  watchlistRawHtml?: string | null;
  // LN-parsed, report-specific follow-up actions (JSON string[]). When present,
  // the FollowUpSection surfaces THESE instead of generic derived suggestions.
  followUpActionsJson?: string | null;
  lexIdFound?: string | null;
  fraudPointScore?: number | null;
  riskIndex_Synthetic?: number | null;
  riskIndex_Stolen?: number | null;
  riskIndex_Manipulated?: number | null;
  riskIndex_VulnerableVictim?: number | null;
  riskIndex_FriendlyFraud?: number | null;
  riskIndex_SuspiciousActivity?: number | null;
  hasRedFlagSection?: boolean;
  lnReportTitle?: string | null;
  /** Driver license number as entered (CIP checklist: LexisNexis cannot verify it, Jessica Beucler 2026-11-02). */
  input_LicenseNumber?: string | null;
  // (Additional fields available on the entity; pull in as the Identity
  // panel UI surfaces them — keeping the wire shape lean for now.)
}

export interface IdentityProviderVerifDetailRow {
  id: number;
  extractId: string;
  fieldName: string;
  fieldOrder: number;
  inputValue?: string | null;
  verifiedValue?: string | null;
  matchStatus: 'matched' | 'mismatched' | 'na';
}

export interface IdentityProviderRedFlagRow {
  id: number;
  extractId: string;
  rowOrder: number;
  category: string;
  iconClass?: string | null;
  alertsJson: string;
  alertsRawHtml?: string | null;
}

export interface IdentityProviderFraudWarningRow {
  id: number;
  extractId: string;
  rowOrder: number;
  inputField: string;
  inputValue?: string | null;
  priorityCode: number;
  warningText: string;
}

export interface GetExtractOutcome {
  success: boolean;
  extract: IdentityProviderExtractRow;
  verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow>;
  redFlags: ReadonlyArray<IdentityProviderRedFlagRow>;
  fraudWarnings: ReadonlyArray<IdentityProviderFraudWarningRow>;
}

/** CHG-0141 — one note of documents a customer provided after LexisNexis could not verify them. */
export interface IdentitySupportingDocumentRow {
  supportingDocumentId: string;
  extractId: string;
  cartId?: string | null;
  /** CIP elements the documents support, comma-separated ("Address,Social Security number"). */
  elements?: string | null;
  documentsProvided: string;
  recordedByUpn: string;
  recordedByName?: string | null;
  recordedAt: string;
}

export interface SupportingDocumentsOutcome {
  success: boolean;
  notes: ReadonlyArray<IdentitySupportingDocumentRow>;
}

export interface AddSupportingDocumentBody {
  documentsProvided: string;
  /** Any of: Name, Address, Date of birth, Social Security number, Driver license number. */
  elements?: ReadonlyArray<string>;
  cartId?: string;
}

export interface AddSupportingDocumentOutcome {
  success: boolean;
  note: IdentitySupportingDocumentRow;
  /** True when the report was already filed with the application and a fresh copy was filed with the note. */
  refiled: boolean;
}

// alpha.98 / BACKLOG-0508 — PersonModal post-import result poll.
// Shape of GET /v3/identity/inquiries/{id}. Lean projection — just the
// fields the PersonModal poll needs to know whether an extract has
// landed and patch the person row.

export interface IdentityVerificationInquiryRow {
  id: string;
  cartId?: string | null;
  inquiryUtc: string;
  cartMode: string;
  aggregateStatus: string;
  createdByOfficerCode?: string | null;
}

export interface GetInquiryOutcome {
  success: boolean;
  inquiry: IdentityVerificationInquiryRow;
  signalCount: number;
  extractCount: number;
  /** Null while waiting for the extension/file-import to land. */
  latestExtract: {
    extract: IdentityProviderExtractRow;
    verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow>;
    redFlags: ReadonlyArray<IdentityProviderRedFlagRow>;
    fraudWarnings: ReadonlyArray<IdentityProviderFraudWarningRow>;
  } | null;
}

// ──────────────────────────────────────────────────────────────────────────
// API surface — client.retail.identity.*
// ──────────────────────────────────────────────────────────────────────────

export class IdentityApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * POST /v3/identity/verify — creates an inquiry + returns the clipboard
   * payload + next-step deeplink. The caller is responsible for the
   * clipboard.writeText + microsoftTeams.executeDeepLink calls.
   */
  public verify(
    body: VerifyInquiryInput,
    opts?: RequestOptions,
  ): Promise<VerifyInquiryOutcome> {
    return this.http.post<VerifyInquiryOutcome>('/v3/identity/verify', { ...opts, body });
  }

  /**
   * POST /v3/identity/import-lexisnexis-html — parser + 4-table persist.
   * Body carries the raw HTML string (the FSR's downloaded LN report).
   */
  public importLexisNexisHtml(
    body: ImportLexisNexisHtmlInput,
    opts?: RequestOptions,
  ): Promise<ImportLexisNexisHtmlOutcome> {
    return this.http.post<ImportLexisNexisHtmlOutcome>(
      '/v3/identity/import-lexisnexis-html',
      { ...opts, body },
    );
  }

  /** GET /v3/identity/extracts/{id} — full retrieval for the Identity panel. */
  public getExtract(
    id: string,
    opts?: RequestOptions,
  ): Promise<GetExtractOutcome> {
    return this.http.get<GetExtractOutcome>(`/v3/identity/extracts/${encodeURIComponent(id)}`, {
      ...opts,
    });
  }

  /**
   * GET /v3/identity/extracts/{id}/report.pdf — the same report as a PDF laid out like the
   * LexisNexis website report (CHG-0141, Bill 2026-11-02). Returns a Blob for a download link.
   */
  public getExtractReportPdf(id: string, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(`/v3/identity/extracts/${encodeURIComponent(id)}/report.pdf`, { ...opts });
  }

  /**
   * GET /v3/identity/extracts/{id}/supporting-documents — CHG-0141: the officer's notes of documents
   * the customer provided after LexisNexis could not verify an identity element. Oldest first.
   */
  public listSupportingDocuments(id: string, opts?: RequestOptions): Promise<SupportingDocumentsOutcome> {
    return this.http.get<SupportingDocumentsOutcome>(
      `/v3/identity/extracts/${encodeURIComponent(id)}/supporting-documents`, { ...opts });
  }

  /**
   * POST /v3/identity/extracts/{id}/supporting-documents — CHG-0141: record what the customer provided
   * (and which CIP elements it supports). Recorded under the signed-in person; never edited.
   */
  public addSupportingDocument(
    id: string,
    body: AddSupportingDocumentBody,
    opts?: RequestOptions,
  ): Promise<AddSupportingDocumentOutcome> {
    return this.http.post<AddSupportingDocumentOutcome>(
      `/v3/identity/extracts/${encodeURIComponent(id)}/supporting-documents`, { ...opts, body });
  }

  /**
   * GET /v3/identity/inquiries/{id} — alpha.98 / BACKLOG-0508.
   * Polled by PersonModal after Run-with-autofill so we can detect when
   * the LN Bridge extension (or drag-drop fallback) has imported the
   * report and persisted the extract. Lean shape — full extract detail
   * goes through getExtract once extractId is known.
   */
  public getInquiry(
    id: string,
    opts?: RequestOptions,
  ): Promise<GetInquiryOutcome> {
    return this.http.get<GetInquiryOutcome>(`/v3/identity/inquiries/${encodeURIComponent(id)}`, {
      ...opts,
    });
  }
}
